import React, { useMemo } from 'react';
import { SourceBadge } from '@components/atoms/SourceBadge';
import { MaterialSource } from '@models/warehouse.model';

type InventoryItem = {
  totalPrice?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  unitCost?: number | null;
  isOwnItem?: boolean;
  isSubcontracted?: boolean;
  originalOrganizationName?: string;
  chargeToClient?: boolean;
  amount?: number;
  name?: string;
  equipmentId?: string;
  materialId?: string;
  time?: string;
  taskName?: string;
  taskId?: string;
  unassigned?: boolean;
  displayOrganizationName?: string;
  costPerHour?: number | null;
  amountType?: string;
  source?: 'WAREHOUSE' | 'MANUAL';
};

type TaskGroup = {
  taskName: string;
  items: InventoryItem[];
};

interface BillOfMaterialsTableProps {
  viewMode: 'full' | 'detailed';
  inventory: InventoryItem[];
  groupedByTask: TaskGroup[];
}

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  return new Date(dateString).toLocaleString('en-US', options);
};

// Table header component
const TableHeader: React.FC<{ showTask?: boolean }> = ({ showTask = true }) => (
  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10 print:bg-gray-100">
    <tr>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Item
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Type
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Amount
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Unit Cost
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Cost/Hour
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Total Cost
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Date
      </th>
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
        Source
      </th>
      {showTask && (
        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
          Task
        </th>
      )}
    </tr>
  </thead>
);

// Table row component
const TableRow: React.FC<{ item: InventoryItem; showTask?: boolean }> = ({
  item,
  showTask = true,
}) => {
  const isEquipment = !!item.equipmentId;
  const type = isEquipment ? 'Equipment' : 'Material';

  const unitCostDisplay = formatCurrency(item.unitCost);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 print:hover:bg-white">
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {item.name || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isEquipment
              ? 'bg-blue-100 text-blue-800 print:bg-blue-50'
              : 'bg-green-100 text-green-800 print:bg-green-50'
          }`}>
          {type}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {item.amount ?? '-'} {item.amountType || ''}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {unitCostDisplay}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {formatCurrency(item.costPerHour)}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
        {formatCurrency(item.totalCost)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {formatDate(item.time)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {item.source ? (
          <SourceBadge source={item.source as MaterialSource} size="sm" />
        ) : (
          '-'
        )}
      </td>
      {showTask && (
        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
          {item.taskName || '-'}
        </td>
      )}
    </tr>
  );
};

// Full Project View - flat list
const FullProjectView: React.FC<{ inventory: InventoryItem[] }> = ({
  inventory,
}) => {
  if (inventory.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No materials or equipment found for this project.
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 print:shadow-none print:border-gray-300">
      <div className="w-full overflow-x-auto max-h-[500px] overflow-y-auto print:max-h-none print:overflow-visible">
        <table className="w-full border-collapse">
          <TableHeader showTask={true} />
          <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
            {inventory.map((item, index) => (
              <TableRow
                key={item.materialId || item.equipmentId || index}
                item={item}
                showTask={true}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Detailed by Task View - grouped with headers
const DetailedByTaskView: React.FC<{ groupedByTask: TaskGroup[] }> = ({
  groupedByTask,
}) => {
  // Calculate subtotals for each task group
  const groupsWithTotals = useMemo(() => {
    return groupedByTask.map((group) => ({
      ...group,
      subtotal: group.items.reduce(
        (sum, item) => sum + (item.totalCost || 0),
        0,
      ),
      itemCount: group.items.length,
    }));
  }, [groupedByTask]);

  if (groupedByTask.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No materials or equipment found for this project.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupsWithTotals.map((group, groupIndex) => (
        <div
          key={group.taskName + groupIndex}
          className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 print:shadow-none print:border-gray-300 print:break-inside-avoid">
          {/* Task Header */}
          <div className="px-4 py-3 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 rounded-t-lg print:bg-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {group.taskName}
              </h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {group.itemCount} item{group.itemCount !== 1 ? 's' : ''}
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Subtotal: {formatCurrency(group.subtotal)}
                </span>
              </div>
            </div>
          </div>

          {/* Task Items Table */}
          <div className="w-full overflow-x-auto print:overflow-visible">
            <table className="w-full border-collapse">
              <TableHeader showTask={false} />
              <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                {group.items.map((item, index) => (
                  <TableRow
                    key={item.materialId || item.equipmentId || index}
                    item={item}
                    showTask={false}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export const BillOfMaterialsTable: React.FC<BillOfMaterialsTableProps> = ({
  viewMode,
  inventory,
  groupedByTask,
}) => {
  if (viewMode === 'full') {
    return <FullProjectView inventory={inventory} />;
  }

  return <DetailedByTaskView groupedByTask={groupedByTask} />;
};

export default BillOfMaterialsTable;
